import { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import GC01 from "./GC01";
import TempFullBase from "./TempFullBase";
import ActiveFaults from "../../pages/ActiveFaults";
import StatusMaintenance from "../status/StatusMaintenance";
import ActiveFaultsCardStack from "../activeFaults/ActiveFaultsCardStack";
import StatusActiveFaults from "../status/StatusActiveFaults";
import { useGetActiveFaults } from "../../hooks/BWOS/useGetActiveFaults";
import WIOSSLoader from "../../interface/WIOSSLoader";
import FaultListItem from "../status/FaultListItem";
import ColorStyle from "./ColorStyle";
import GC02 from "./GC02";

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 60rem;
  gap: 24px;
  height: calc(88vh);
  min-height: 0; /* allow children to shrink in flex/grid */
`;

const Box = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  padding: 0.75rem; /* ← tiny border/margin */
  overflow: hidden;
  box-shadow: var(--shadow-div-sm);
  border-radius: 0.75rem;
  // overflow: hidden; /* ← trims the SVG edges cleanly */
`;

const DrawingWrapper = styled.div`
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

const SvgWrapper = styled.div`
  position: absolute;
  bottom: 2rem; /* anchors whole block from bottom */
  left: 0;
  width: 100%;
  /* Optional: let it fill the Box and bottom-align within */
  height: calc(100% - 2rem);
  display: flex;

  justify-content: center; /* center horizontally */
  align-items: flex-end; /* push SVG to the bottom of this area */
  z-index: 999;
  pointer-events: auto; /* set to auto if you need clicks */
`;

const Svg = styled.svg`
  width: 100%;
  max-height: 40%;
  max-width: 1400px; /* optional guard */

  display: block;
  background: transparent;

  opacity: 0.7;
`;

const StyledHeading = styled.h5`
  color: var(--color-grey-500);

  padding-left: 2em;
`;

const NoContentContainer = styled.div`
  display: flex;
  width: 30%;
  height: 30%;
  align-items: top;
  justify-content: center;
`;

const FaultContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 1rem;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding-left: 1rem;
  padding-bottom: 1rem;
  margin-top: 0.4rem;
  overflow-x: auto; /* Optional: Allows horizontal scrolling if there are many cards */
`;

function SiteFaultMap() {
  const { data, isLoading, isError, error } = useGetActiveFaults();

  // Build a Set of GC ids that have an active fault (e.g. "01","02","61",…)
  const activeGCs = useMemo(() => {
    const set = new Set();
    const rows = data?.data ?? [];
    for (const r of rows) {
      // Prefer explicit r.gc if present
      if (r?.gc != null) {
        const gc = String(r.gc).trim().padStart(2, "0"); // "1" -> "01"
        set.add(gc);
        continue;
      }
      // Fallback: try to parse from locationRaw like "COM012:73_43.11/SC"
      const m = String(r?.locationRaw ?? "").match(/:(\d{1,3})[_/]/);
      if (m) set.add(m[1].padStart(2, "0"));
    }
    return set;
  }, [data]);

  console.log("Active GC", activeGCs);

  // Helper for components
  const statusFor = (gcId /* e.g. "01" */) =>
    activeGCs.has(gcId) ? "red" : "blue";

  // const hasFaults = !!data?.data?.length;
  // const scheme = hasFaults ? "red" : "blue";

  // full original artboard
  const VB_W = 7401.99;
  const VB_H = 2263.01;

  if (data && data.noContent) return <div>No Faults.</div>;
  if (isLoading) return <WIOSSLoader />;
  if (isError) return <div>Error: {error.message}</div>;
  if (!data.data)
    return (
      <NoContentContainer>
        <WIOSSLoader text="NO FAULTS FOUND" />
      </NoContentContainer>
    );

  return (
    <Box>
      <SvgWrapper>
        <Svg
          viewBox={`0 0 ${VB_W} ${VB_H}`} // full artboard
          preserveAspectRatio="xMidYMax meet" // bottom-align when aspect ratios differ
          shapeRendering="geometricPrecision"
          data-status="blue" // default palette
        >
          <ColorStyle />
          <TempFullBase />

          <GC01
            status="yellow"
            onClick={(gcId) => {
              console.log("User Clicked GC", gcId); // -> "01"
            }}
          />
          <GC02
            status="red-blink"
            onClick={(gcId) => {
              console.log("User Clicked GC", gcId);
            }}
          />

          {/* <g data-status={statusFor("01")}>
            <GC01 />
          </g> */}
        </Svg>
      </SvgWrapper>

      <FaultContainer>
        <StyledHeading>Active Faults</StyledHeading>
        {data.data.map((fault) => (
          <FaultListItem key={fault.id} fault={fault} />
        ))}
      </FaultContainer>
    </Box>
  );
}

export default SiteFaultMap;
